import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';

const UNIT_OPTIONS = [
  { value: 'pieza',    label: 'Pieza' },
  { value: 'litro',    label: 'Litro' },
  { value: 'par',      label: 'Par' },
  { value: 'juego',    label: 'Juego' },
  { value: 'servicio', label: 'Servicio' },
  { value: 'metro',    label: 'Metro' },
];

const defaultForm = {
  sku: '', productType: '', category: '', subcategory: '',
  brand: '', name: '', description: '', unit: 'pieza',
  salePrice: '', active: true,
};

// ─── Inline Product Type Dropdown ────────────────────────────────────────────
const ProductTypeDropdown = ({ value, onChange }) => {
  const [open, setOpen]           = useState(false);
  const [mode, setMode]           = useState('select'); // 'select' | 'add' | 'edit'
  const [types, setTypes]         = useState([]);
  const [allTypes, setAllTypes]   = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  // Add mode state
  const [newLabel, setNewLabel]   = useState('');
  const [saving, setSaving]       = useState(false);
  const [addError, setAddError]   = useState('');

  // Edit mode state
  const [editRows, setEditRows]   = useState([]);
  const [savingRow, setSavingRow] = useState(null);

  const containerRef = useRef(null);

  // ── Fetch active types (for select mode)
  const fetchActiveTypes = useCallback(async () => {
    setLoadingTypes(true);
    const { data } = await supabase?.from('product_types')?.select('*')?.eq('active', true)?.order('label');
    setTypes(data || []);
    setLoadingTypes(false);
  }, []);

  // ── Fetch all types (for edit mode)
  const fetchAllTypes = useCallback(async () => {
    const { data } = await supabase?.from('product_types')?.select('*')?.order('label');
    setAllTypes(data || []);
    setEditRows((data || [])?.map(t => ({ ...t, _label: t?.label, _active: t?.active })));
  }, []);

  // Load active types on mount
  useEffect(() => { fetchActiveTypes(); }, [fetchActiveTypes]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setOpen(false);
        setMode('select');
        setNewLabel('');
        setAddError('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedLabel = types?.find(t => t?.value === value)?.label
    || allTypes?.find(t => t?.value === value)?.label
    || '';

  // ── Select a type
  const handleSelect = (type) => {
    onChange(type?.value);
    setOpen(false);
    setMode('select');
  };

  // ── Switch to add mode
  const handleOpenAdd = () => {
    setMode('add');
    setNewLabel('');
    setAddError('');
  };

  // ── Switch to edit mode
  const handleOpenEdit = async () => {
    setMode('edit');
    await fetchAllTypes();
  };

  // ── Derive value slug from label
  const toValue = (label) =>
    label?.toLowerCase()?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.replace(/[^a-z0-9]+/g, '_')?.replace(/^_|_$/g, '');

  // ── Save new type
  const handleSaveNew = async () => {
    const trimmed = newLabel?.trim();
    if (!trimmed) { setAddError('El nombre no puede estar vacío'); return; }
    const slug = toValue(trimmed);
    setSaving(true);
    setAddError('');
    const { data, error } = await supabase?.from('product_types')?.insert({ value: slug, label: trimmed, active: true })?.select()?.single();
    setSaving(false);
    if (error) {
      setAddError(error?.code === '23505' ? 'Ya existe un tipo con ese nombre' : error?.message);
      return;
    }
    await fetchActiveTypes();
    onChange(data?.value);
    setNewLabel('');
    setMode('select');
    setOpen(false);
  };

  // ── Save edited row
  const handleSaveRow = async (row) => {
    setSavingRow(row?.id);
    const { error } = await supabase?.from('product_types')?.update({ label: row?._label, active: row?._active, updated_at: new Date()?.toISOString() })?.eq('id', row?.id);
    setSavingRow(null);
    if (!error) {
      await fetchActiveTypes();
      await fetchAllTypes();
      // If the currently selected type was deactivated, clear selection
      if (!row?._active && value === row?.value) onChange('');
    }
  };

  const updateEditRow = (id, field, val) =>
    setEditRows(prev => prev?.map(r => r?.id === id ? { ...r, [field]: val } : r));

  return (
    <div ref={containerRef} className="relative">
      <label className="text-sm font-medium text-foreground block mb-1.5">Tipo de Producto</label>
      <button
        type="button"
        onClick={() => { setOpen(o => !o); if (!open) { setMode('select'); } }}
        className="w-full flex items-center justify-between h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className={selectedLabel ? 'text-foreground' : 'text-muted-foreground'}>
          {selectedLabel || 'Seleccionar tipo'}
        </span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">

          {/* ── SELECT MODE ── */}
          {mode === 'select' && (
            <div className="max-h-64 overflow-y-auto">
              {loadingTypes ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">Cargando...</div>
              ) : types?.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">Sin tipos activos</div>
              ) : (
                types?.map(type => (
                  <button
                    key={type?.id}
                    type="button"
                    onClick={() => handleSelect(type)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                      value === type?.value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
                    }`}
                  >
                    {type?.label}
                  </button>
                ))
              )}
              <div className="border-t border-border">
                <button
                  type="button"
                  onClick={handleOpenAdd}
                  className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-muted transition-colors flex items-center gap-2"
                >
                  <Icon name="Plus" size={14} />
                  Agregar tipo
                </button>
                <button
                  type="button"
                  onClick={handleOpenEdit}
                  className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors flex items-center gap-2"
                >
                  <Icon name="Pencil" size={14} />
                  Editar tipos
                </button>
              </div>
            </div>
          )}

          {/* ── ADD MODE ── */}
          {mode === 'add' && (
            <div className="p-3 space-y-3">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Nuevo tipo de producto</p>
              <input
                autoFocus
                type="text"
                placeholder="Nombre del tipo"
                value={newLabel}
                onChange={e => { setNewLabel(e?.target?.value); setAddError(''); }}
                onKeyDown={e => e?.key === 'Enter' && handleSaveNew()}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              {addError && <p className="text-xs text-error">{addError}</p>}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSaveNew}
                  disabled={saving}
                  className="flex-1 h-8 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Guardando...' : 'Guardar tipo'}
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('select'); setNewLabel(''); setAddError(''); }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* ── EDIT MODE ── */}
          {mode === 'edit' && (
            <div className="max-h-80 overflow-y-auto">
              <div className="px-3 py-2 border-b border-border">
                <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Editar tipos</p>
              </div>
              {editRows?.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">Sin tipos registrados</div>
              ) : (
                editRows?.map(row => (
                  <div key={row?.id} className="px-3 py-2 border-b border-border last:border-0">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={row?._label}
                        onChange={e => updateEditRow(row?.id, '_label', e?.target?.value)}
                        className="flex-1 h-8 rounded border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      />
                      {/* Toggle activo/inactivo */}
                      <button
                        type="button"
                        onClick={() => updateEditRow(row?.id, '_active', !row?._active)}
                        className={`flex-shrink-0 flex items-center gap-1 px-2 h-8 rounded text-xs font-medium transition-colors ${
                          row?._active
                            ? 'bg-success/10 text-success hover:bg-success/20' :'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                        title={row?._active ? 'Activo — clic para desactivar' : 'Inactivo — clic para activar'}
                      >
                        <Icon name={row?._active ? 'ToggleRight' : 'ToggleLeft'} size={14} />
                        {row?._active ? 'Activo' : 'Inactivo'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveRow(row)}
                        disabled={savingRow === row?.id}
                        className="flex-shrink-0 h-8 px-2 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                      >
                        {savingRow === row?.id ? '...' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                ))
              )}
              <div className="px-3 py-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => { setMode('select'); }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  ← Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── ProductModal ─────────────────────────────────────────────────────────────
const ProductModal = ({ open, product, onClose, onSave, loading = false }) => {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (product) {
      setForm({
        sku:         product?.sku || '',
        productType: product?.productType || '',
        category:    product?.category || '',
        subcategory: product?.subcategory || '',
        brand:       product?.brand || '',
        name:        product?.name || '',
        description: product?.description || '',
        unit:        product?.unit || 'pieza',
        salePrice:   product?.salePrice?.toString() || '',
        active:      product?.active !== false,
      });
    } else {
      setForm(defaultForm);
    }
    setErrors({});
  }, [product, open]);

  const validate = () => {
    const errs = {};
    if (!form?.name?.trim()) errs.name = 'El nombre es requerido';
    if (form?.salePrice && isNaN(parseFloat(form?.salePrice))) errs.salePrice = 'Precio inválido';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validate()) return;
    onSave?.({
      ...form,
      salePrice: parseFloat(form?.salePrice) || 0,
    });
  };

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-foreground">
            {product ? 'Editar Producto' : 'Nuevo Producto'}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="SKU"
              placeholder="Ej. REF-001"
              value={form?.sku}
              onChange={e => set('sku', e?.target?.value)}
            />
            <ProductTypeDropdown
              value={form?.productType}
              onChange={v => set('productType', v)}
            />
          </div>

          <Input
            label="Nombre *"
            placeholder="Nombre del producto"
            value={form?.name}
            onChange={e => set('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Marca"
              placeholder="Ej. Bosch, NGK"
              value={form?.brand}
              onChange={e => set('brand', e?.target?.value)}
            />
            <Input
              label="Categoría"
              placeholder="Ej. Motor, Frenos"
              value={form?.category}
              onChange={e => set('category', e?.target?.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Subcategoría"
              placeholder="Ej. Bujías, Pastillas"
              value={form?.subcategory}
              onChange={e => set('subcategory', e?.target?.value)}
            />
            <Select
              label="Unidad"
              options={UNIT_OPTIONS}
              value={form?.unit}
              onChange={v => set('unit', v)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Precio de Venta"
              type="number"
              placeholder="0.00"
              value={form?.salePrice}
              onChange={e => set('salePrice', e?.target?.value)}
              error={errors?.salePrice}
            />
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form?.active}
                  onChange={e => set('active', e?.target?.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                <span className="text-sm font-medium text-foreground">Activo</span>
              </label>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground block mb-2">Descripción</label>
            <textarea
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              rows={3}
              placeholder="Descripción del producto..."
              value={form?.description}
              onChange={e => set('description', e?.target?.value)}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" loading={loading}>
              <Icon name="Save" size={16} className="mr-2" />
              Guardar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductModal;
